import React, { useMemo, useState } from "react";
import ModalPopup from "../../../../../components/popups/DefaultPopUp";
import StatusBadge from "../../../../../utils/StatusBadge";
import ActionButtons from "../../../../../utils/ActionButtons";
import "../../../../../css/pages/ca_pages/models/client_followups/DocumentRequestsList.css";

/*
 * ---------------------------------------------------------------------------
 * Helpers
 * ---------------------------------------------------------------------------
 */

const getStatus = (request) => {
  return request?.status || request?.request_status || "pending";
};

const getClientId = (request) => {
  return request?.client || request?.client_id || request?.client?.id || null;
};

const getReminders = (request) => {
  if (Array.isArray(request?.reminders)) {
    return request.reminders;
  }

  return [];
};

const getEnabledReminderCount = (request) => {
  const reminders = getReminders(request);

  return reminders.filter((reminder) => {
    /*
     * A reminder is considered enabled when it is not explicitly
     * disabled.
     */
    if (reminder?.is_enabled === false) {
      return false;
    }

    if (Array.isArray(reminder?.deliveries)) {
      return reminder.deliveries.some(
        (delivery) => delivery?.is_enabled !== false,
      );
    }

    return true;
  }).length;
};

const getNextReminder = (request) => {
  const reminders = getReminders(request);

  if (!reminders.length) {
    return null;
  }

  const now = Date.now();

  const upcoming = reminders
    .filter((reminder) => {
      if (!reminder?.scheduled_for) {
        return false;
      }

      if (reminder?.is_enabled === false) {
        return false;
      }

      return new Date(reminder.scheduled_for).getTime() >= now;
    })
    .sort(
      (a, b) =>
        new Date(a.scheduled_for).getTime() -
        new Date(b.scheduled_for).getTime(),
    );

  return upcoming[0] || null;
};

// const getDeliveryChannels = (reminder) => {
//   if (!Array.isArray(reminder?.deliveries)) { return []; }

//   return reminder.deliveries
//     .filter((delivery) => delivery?.is_enabled !== false)
//     .map((delivery) => delivery?.platform)
//     .filter(Boolean);
// };

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value, timezone) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: timezone || undefined,
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
};

const getRequestExpiry = (request) => {
  /*
   * Support both possible API representations:
   *
   * request_expiry_days
   * request_expiry_date
   */
  if (request?.request_expiry_date) {
    return formatDate(request.request_expiry_date);
  }

  if (request?.expires_at) {
    return formatDate(request.expires_at);
  }

  return null;
};

/*
 * ---------------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------------------
 */

const DocumentRequestsList = ({
  data = [],
  onView,
  onDelete,
  deletingRequestId = null,
}) => {
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);

  const [selectedRecord, setSelectedRecord] = useState(null);

  /*
   * -----------------------------------------------------------------------
   * Summary statistics
   * -----------------------------------------------------------------------
   */

  const summary = useMemo(() => {
    const total = data.length;

    const pending = data.filter((item) => getStatus(item) === "pending").length;

    const completed = data.filter(
      (item) => getStatus(item) === "completed",
    ).length;

    const overdue = data.filter((item) => getStatus(item) === "overdue").length;

    const expired = data.filter((item) => getStatus(item) === "expired").length;

    const withReminders = data.filter(
      (item) => getReminders(item).length > 0,
    ).length;

    return {
      total,
      pending,
      completed,
      overdue,
      expired,
      withReminders,
    };
  }, [data]);

  /*
   * -----------------------------------------------------------------------
   * Empty state
   * -----------------------------------------------------------------------
   */

  if (!data.length) {
    return (
      <div className="empty-state">
        <div className="empty-state__icon">📄</div>

        <h3>No document requests found</h3>

        <p>
          Create your first document request to start collecting documents from
          your clients.
        </p>
      </div>
    );
  }

  /*
   * -----------------------------------------------------------------------
   * Actions
   * -----------------------------------------------------------------------
   */

  const handleView = (clientId, docRequestId) => {
    if (!clientId || !docRequestId) {
      return;
    }

    onView(clientId, docRequestId);
  };

  const handleDelete = (clientId, docRequestId) => {
    if (!clientId || !docRequestId) {
      return;
    }

    setSelectedClient(clientId);

    setSelectedRecord(docRequestId);

    setShowDeletePopup(true);
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setSelectedClient(null);
    setSelectedRecord(null);
  };

  const confirmDeleteItem = async () => {
    if (!selectedClient || !selectedRecord) {
      return;
    }

    try {
      await onDelete(selectedClient, selectedRecord);
    } finally {
      closeDeletePopup();
    }
  };

  /*
   * -----------------------------------------------------------------------
   * Render
   * -----------------------------------------------------------------------
   */

  return (
    <>
      <div className="ca-requests-wrapper">
        {/* =========================================================
                    SUMMARY
                ========================================================== */}

        <div className="ca-requests-summary">
          <span>
            Total: <strong>{summary.total}</strong>
          </span>

          <span>
            Pending: <strong>{summary.pending}</strong>
          </span>

          <span>
            Completed: <strong>{summary.completed}</strong>
          </span>

          {summary.overdue > 0 && (
            <span>
              Overdue: <strong>{summary.overdue}</strong>
            </span>
          )}

          {summary.expired > 0 && (
            <span>
              Expired: <strong>{summary.expired}</strong>
            </span>
          )}

          <span>
            With reminders: <strong>{summary.withReminders}</strong>
          </span>
        </div>

        {/* =========================================================
                    TABLE
                ========================================================== */}

        <div className="ca-requests-table-container">
          <table className="ca-requests-table">
            <thead>
              <tr>
                <th>Client</th>

                <th>Request</th>

                <th>Type</th>

                <th>Due Date</th>

                <th>Reminders</th>

                <th>Expiry</th>

                <th>Status</th>

                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {data.map((req) => {
                const clientId = getClientId(req);

                const status = getStatus(req);

                const reminders = getReminders(req);

                const nextReminder = getNextReminder(req);

                const reminderCount = getEnabledReminderCount(req);

                const expiry = getRequestExpiry(req);

                const isDeleting = String(deletingRequestId) === String(req.id);

                return (
                  <tr
                    key={req.id}
                    className={isDeleting ? "ca-request-row--deleting" : ""}
                  >
                    {/* =================================
                                                CLIENT
                                            ================================== */}

                    <td>
                      <div className="ca-requests-cell-primary">
                        <strong>
                          {req.client_name ||
                            req.client?.name ||
                            req.client?.user_full_name ||
                            "—"}
                        </strong>

                        <small>
                          {req.client_email || req.client?.email || "—"}
                        </small>
                      </div>
                    </td>

                    {/* =================================
                                                REQUEST
                                            ================================== */}

                    <td>
                      <div className="ca-requests-cell-primary">
                        <strong>{req.title || "—"}</strong>

                        <small>
                          {req.description
                            ? req.description.length > 50
                              ? `${req.description.slice(0, 50)}...`
                              : req.description
                            : "No description"}
                        </small>
                      </div>
                    </td>

                    {/* =================================
                                                DOCUMENT TYPE
                                            ================================== */}

                    <td>{req.document_type || req.documentType || "—"}</td>

                    {/* =================================
                                                DUE DATE
                                            ================================== */}

                    <td>
                      <div className="ca-requests-date-cell">
                        <strong>{formatDate(req.due_date)}</strong>

                        {req.due_date && (
                          <small>
                            {new Date(req.due_date).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </small>
                        )}
                      </div>
                    </td>

                    {/* =================================
                                                REMINDERS
                                            ================================== */}

                    <td>
                      {reminders.length === 0 ? (
                        <div className="ca-reminder-summary ca-reminder-summary--none">
                          <span>—</span>

                          <small>None</small>
                        </div>
                      ) : (
                        <div className="ca-reminder-summary">
                          <strong>
                            {reminderCount}{" "}
                            {reminderCount === 1 ? "reminder" : "reminders"}
                          </strong>

                          {nextReminder && (
                            <small>
                              Next:{" "}
                              {formatDateTime(
                                nextReminder.scheduled_for,
                                nextReminder.timezone,
                              )}
                            </small>
                          )}

                          {!nextReminder && <small>No upcoming reminder</small>}
                        </div>
                      )}
                    </td>

                    {/* =================================
                                                EXPIRY
                                            ================================== */}

                    <td>
                      <div className="ca-requests-date-cell">
                        {expiry ? (
                          <>
                            <strong>{expiry}</strong>

                            <small>Request expiry</small>
                          </>
                        ) : req.request_expiry_days ? (
                          <>
                            <strong>{req.request_expiry_days} days</strong>

                            <small>After creation</small>
                          </>
                        ) : (
                          "—"
                        )}
                      </div>
                    </td>

                    {/* =================================
                                                STATUS
                                            ================================== */}

                    <td>
                      <StatusBadge status={status} />
                    </td>

                    {/* =================================
                                                ACTIONS
                                            ================================== */}

                    <td>
                      <ActionButtons
                        actions={[
                          {
                            buttonType: "view",

                            onClick: () => handleView(clientId, req.id),
                          },

                          {
                            buttonType: "delete",

                            onClick: () => handleDelete(clientId, req.id),

                            disabled: isDeleting,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* =============================================================
                DELETE CONFIRMATION
            ============================================================= */}

      <ModalPopup
        isOpen={showDeletePopup}
        onClose={closeDeletePopup}
        title="Delete Document Request"
        subtext={
          "Are you sure you want to delete this document request? This action cannot be undone."
        }
        actionText={deletingRequestId ? "Deleting..." : "Delete"}
        onAction={confirmDeleteItem}
      />
    </>
  );
};

export default DocumentRequestsList;
